/**
 * Coverage vehicle review service.
 *
 * Submits the completed live ASR + tarjeta evidence package to the server-side
 * review pipeline. The edge function owns validation, email delivery, secure
 * review-link creation, and the vehicle review lifecycle.
 */
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'

interface CoverageReviewRow {
  id: string
  vehicle_id: string
  status: string
  due_at: string | null
  result_summary: string | null
  last_error: string | null
}

interface SubmitCoverageVehicleReviewResponse {
  review: CoverageReviewRow | null
  partnerConfigured?: boolean
  coverageApplicable?: boolean
  delivery?: {
    sent: boolean
    reason?: string
    provider?: string
    vehicleCategory?: string
  }
}

interface EdgeFunctionErrorBody {
  message: string | null
  reasons: string[]
  code?: string | null
  statusCode?: number
}

async function readEdgeFunctionErrorBody(err: unknown): Promise<EdgeFunctionErrorBody> {
  const ctx = (
    err as {
      context?: {
        status?: number
        clone?: () => {
          json?: () => Promise<unknown>
          text?: () => Promise<string>
        }
      }
    }
  ).context
  if (!ctx || typeof ctx.clone !== 'function') return { message: null, reasons: [] }

  try {
    const body = (await ctx.clone().json?.()) as {
      error?: unknown
      code?: unknown
      message?: unknown
      reasons?: unknown
    }
    const reasons = Array.isArray(body?.reasons)
      ? body.reasons.filter((reason): reason is string => typeof reason === 'string')
      : []
    return {
      message:
        coverageReviewFriendlyMessage(reasons[0]) ??
        coverageReviewFriendlyMessage(typeof body?.error === 'string' ? body.error : null) ??
        coverageReviewFriendlyMessage(typeof body?.message === 'string' ? body.message : null),
      reasons,
      code: typeof body?.code === 'string' ? body.code : null,
      statusCode: ctx.status,
    }
  } catch {
    try {
      const text = await ctx.clone().text?.()
      return {
        message: coverageReviewFriendlyMessage(text ? text.slice(0, 240) : null),
        reasons: [],
        statusCode: ctx.status,
      }
    } catch {
      return { message: null, reasons: [], code: null, statusCode: ctx.status }
    }
  }
}

function coverageReviewFriendlyMessage(message: string | null | undefined): string | null {
  if (!message) return null
  if (/17.+ASR|ASR.+17|live ASR/i.test(message)) {
    return 'Completá las 17 fotos de inspección desde la cámara de Tago antes de enviar el vehículo.'
  }
  if (/tarjeta/i.test(message)) {
    return 'Capturá frente y reverso de la tarjeta de circulación desde la cámara de Tago.'
  }
  if (/email|resend|review delivery|fallback/i.test(message)) {
    return 'No pudimos enviar la revisión de cobertura. Intentá de nuevo en unos minutos.'
  }
  if (/INSPECTION_PHOTOS_NEED_RETAKE|LOW_LIGHT|UNDEREXPOSED|more light|poca luz/i.test(message)) {
    return 'Las fotos exteriores necesitan más luz. Repetilas de día o activá la linterna.'
  }
  return message
}

async function serviceErrorFromFunctionError(
  error: { message: string; code?: string; status?: number },
  code: string,
): Promise<ServiceError> {
  const serverBody = await readEdgeFunctionErrorBody(error)
  return new ServiceError({
    message: serverBody.message ?? coverageReviewFriendlyMessage(error.message) ?? error.message,
    code,
    statusCode: error.status ?? serverBody.statusCode ?? 500,
    details: { reasons: serverBody.reasons, serverCode: serverBody.code },
    cause: error,
  })
}

class CoverageReviewServiceImpl {
  async submitVehicleReview(vehicleId: string): Promise<SubmitCoverageVehicleReviewResponse> {
    const { data, error } = await supabase.functions.invoke<SubmitCoverageVehicleReviewResponse>(
      'submit-coverage-vehicle-review',
      {
        body: { vehicleId },
      },
    )

    if (error) throw await serviceErrorFromFunctionError(error, 'COVERAGE_REVIEW_SUBMIT_FAILED')
    if (!data?.review?.id && data?.coverageApplicable !== false) {
      throw new ServiceError({
        message: 'No pudimos crear la revisión de cobertura. Intentá de nuevo.',
        code: 'COVERAGE_REVIEW_SUBMIT_FAILED',
        statusCode: 502,
      })
    }

    return data
  }
}

export const vehicleCoverageReviewService = new CoverageReviewServiceImpl()
